/**
 * ADR-0014 Phase 4 — Surface-first boot.
 * Resolves which UI surface is intended so deferred warmups stay scoped.
 */

import { loadRoleForBusiness } from '../../utils/roleStorage';
import { getCurrentBusinessId } from '../../utils/sharedCore';

export const BOOT_SURFACES = Object.freeze({
  POS: 'pos',
  MANAGER: 'manager',
  INVENTORY: 'inventory',
  STOCK: 'stock',
  REPORTS: 'reports',
  GENERIC: 'generic'
});

/**
 * @returns {string} pathname without hash prefix, e.g. "/inventory"
 */
export function getHashPathname() {
  if (typeof window === 'undefined') return '/';
  const raw = window.location.hash || '';
  const withoutHash = raw.startsWith('#') ? raw.slice(1) : raw;
  const path = withoutHash.split('?')[0] || '/';
  return path.startsWith('/') ? path : `/${path}`;
}

/**
 * Peek persisted role type without React (boot-safe).
 * @param {string} [businessId]
 * @returns {'manager'|'worker'|'admin'|null}
 */
export function peekRoleType(businessId) {
  try {
    const bid =
      businessId ||
      getCurrentBusinessId({ allowFallback: false }) ||
      null;
    if (!bid) return null;
    const { role } = loadRoleForBusiness(bid);
    return role?.type || null;
  } catch {
    return null;
  }
}

/**
 * @param {{ businessId?: string, roleType?: string|null, pathname?: string }} [opts]
 * @returns {typeof BOOT_SURFACES[keyof typeof BOOT_SURFACES]}
 */
export function resolveBootSurface(opts = {}) {
  const pathname = opts.pathname ?? getHashPathname();
  const roleType = opts.roleType ?? peekRoleType(opts.businessId);

  if (pathname.startsWith('/reports') || pathname.startsWith('/payment-report')) {
    return BOOT_SURFACES.REPORTS;
  }
  if (pathname.startsWith('/inventory')) {
    return BOOT_SURFACES.INVENTORY;
  }
  if (pathname.startsWith('/stock')) {
    return BOOT_SURFACES.STOCK;
  }
  if (pathname === '/' || pathname === '') {
    if (roleType === 'manager') return BOOT_SURFACES.MANAGER;
    return BOOT_SURFACES.POS;
  }
  // Other business pages: stay light unless manager home-like warm is useful
  if (roleType === 'manager') return BOOT_SURFACES.GENERIC;
  return BOOT_SURFACES.GENERIC;
}

/**
 * What deferred heavy init may compute for a surface.
 * Stock projection is always computed sync in initialize(fastBoot) — never again here.
 * Worker POS never warms manager KPIs, sales analytics, or full reports at boot.
 *
 * @param {string} surface
 * @returns {{
 *   filters: boolean,
 *   stock: boolean,
 *   workerDashboard: boolean,
 *   managerDashboard: boolean,
 *   reports: boolean,
 *   sales: boolean,
 *   purchases: boolean
 * }}
 */
export function getDeferredWarmPlan(surface) {
  switch (surface) {
    case BOOT_SURFACES.POS:
      return {
        filters: true,
        stock: false,
        workerDashboard: false,
        managerDashboard: false,
        reports: false,
        sales: false,
        purchases: false
      };
    case BOOT_SURFACES.MANAGER:
      // Dashboard query owns inventoryMetrics + managerDashboard on `/`.
      // Avoid double FIFO/sales/purchases warm competing with first paint.
      return {
        filters: true,
        stock: false,
        workerDashboard: false,
        managerDashboard: false,
        reports: false,
        sales: false,
        purchases: false
      };
    case BOOT_SURFACES.REPORTS:
      // Payment/Reports pages own their aggregates from DataContext —
      // do not persist manager/sales/purchases warms into InstantCache (fights Inventaire).
      return {
        filters: true,
        stock: false,
        workerDashboard: false,
        managerDashboard: false,
        reports: false,
        sales: false,
        purchases: false
      };
    case BOOT_SURFACES.INVENTORY:
    case BOOT_SURFACES.STOCK:
      // Page hooks own lot enrich / stock read-models — InstantCache only keeps filter shell.
      return {
        filters: true,
        stock: false,
        workerDashboard: false,
        managerDashboard: false,
        reports: false,
        sales: false,
        purchases: false
      };
    case BOOT_SURFACES.GENERIC:
    default:
      return {
        filters: true,
        stock: false,
        workerDashboard: false,
        managerDashboard: false,
        reports: false,
        sales: false,
        purchases: false
      };
  }
}
